import type { FC, ReactNode } from 'react'
import type { PostSummary } from '@/types'

import Link from 'next/link'
import { cn } from '@/lib/utils'
import { CategoryLink } from '@/components/blog/category-link'
import { CoverImage } from '@/components/blog/cover-image'
import { PostMeta } from '@/components/blog/post-meta'

interface PostCardProps {
    post: PostSummary
    variant?: 'grid' | 'list'
    headingLevel?: 'h2' | 'h3'
    showExcerpt?: boolean
    priority?: boolean
    className?: string
}

const titleLinkClass = 'rounded-sm underline-offset-[0.2em] decoration-brand/70 decoration-1 transition-colors after:absolute after:inset-0 after:rounded-xl group-hover:underline focus-visible:outline-none focus-visible:after:outline-2 focus-visible:after:outline-offset-4 focus-visible:after:outline-ring'

const imageClass = 'transition-transform duration-700 ease-out group-hover:scale-[1.035] motion-reduce:transition-none motion-reduce:group-hover:scale-100'

export const PostCard: FC<PostCardProps> = ({
    post,
    variant = 'grid',
    headingLevel = 'h3',
    showExcerpt = true,
    priority = false,
    className
}): ReactNode => {
    const Heading = headingLevel
    const href = `/blog/${post.slug}`

    if (variant === 'list') {
        return (
            <article className={cn('group relative grid grid-cols-[7rem_1fr] gap-5 sm:grid-cols-[13rem_1fr] sm:gap-8', className)}>
                <div className='relative aspect-[4/3] overflow-hidden rounded-lg bg-muted'>
                    <CoverImage src={post.coverImage} alt={post.coverImageAlt} priority={priority} className={imageClass} />
                </div>

                <div className='flex min-w-0 flex-col'>
                    {post.category && <CategoryLink category={post.category} />}

                    <Heading className='mt-2 font-heading text-xl font-medium leading-snug tracking-tight text-balance sm:text-2xl'>
                        <Link href={href} className={titleLinkClass}>
                            {post.title}
                        </Link>
                    </Heading>

                    {showExcerpt && post.excerpt && (
                        <p className='mt-2 hidden text-[0.95rem] leading-relaxed text-muted-foreground line-clamp-2 sm:block'>
                            {post.excerpt}
                        </p>
                    )}

                    <PostMeta
                        author={post.author}
                        publishedAt={post.publishedAt}
                        publishedLabel={post.publishedLabel}
                        readingTime={post.readingTime}
                        className='mt-4'
                    />
                </div>
            </article>
        )
    }

    return (
        <article className={cn('group relative flex flex-col', className)}>
            <div className='relative aspect-[4/3] overflow-hidden rounded-xl bg-muted'>
                <CoverImage src={post.coverImage} alt={post.coverImageAlt} priority={priority} className={imageClass} />
            </div>

            <div className='mt-5 flex flex-1 flex-col'>
                {post.category && <CategoryLink category={post.category} />}

                <Heading className='mt-2.5 font-heading text-2xl font-medium leading-[1.15] tracking-tight text-balance'>
                    <Link href={href} className={titleLinkClass}>
                        {post.title}
                    </Link>
                </Heading>

                {showExcerpt && post.excerpt && (
                    <p className='mt-3 text-[0.95rem] leading-relaxed text-muted-foreground line-clamp-3'>
                        {post.excerpt}
                    </p>
                )}

                <PostMeta
                    author={post.author}
                    publishedAt={post.publishedAt}
                    publishedLabel={post.publishedLabel}
                    readingTime={post.readingTime}
                    className='mt-auto pt-5'
                />
            </div>
        </article>
    )
}
